import React from "react";
import {
  Box,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Button,
  Typography,
  Drawer,
  Menu,
  MenuItem,
} from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { useLocation, useNavigate } from "react-router-dom";
import CompanyLogo from "../../assets/svgIcons/company-logo.svg";
import Smalllogo from "../../assets/svgIcons/company-sidebar-logo.svg";
import HelpIcon from "../../assets/svgIcons/help.svg";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../services/redux/store";
import {
  clearSavedBasicCoApplicants,
  setAadhaarSeedingStatus,
  setActiveHorizontalIndex,
  setDisableAddMoreCoApplicant,
  setHorizontalSteps,
  setIsBypassLogin,
  setIsEligible,
} from "../../services/redux/slices/stepperDynamicFormSlice";
import ModalPopup from "../../components/ui/ModalPopup";
import { PrimaryButton } from "../../components/ui/Button";
import { clearAllApplicantLoanDetails } from "../../services/redux/slices/applicationLoanDetailsSlice";
import { ROUTES } from "../../routes/routerConfig";
import { setSelectedIndex } from "../../services/redux/slices/tabsSlice";
import { clearPartPaymentType } from "../../services/redux/slices/documentFormSlice";
import { cleardisbursementButtonSlice } from "../../services/redux/slices/disbursementButtonSlice";

export interface SidebarNavItem {
  id: string;
  icon: React.ReactNode;
  path: string;
  label: string;
  children?: { id: string; path: string; label: string }[];
}

interface SidebarProps {
  navItems: SidebarNavItem[];
  onLogout?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ navItems }) => {
  const [confirmModalOpen, setConfirmModalOpen] = React.useState(false);
  const [pendingPath, setPendingPath] = React.useState("");
  const [sidebarOpen, setSidebarOpen] = React.useState(false);
  const [hoveredQuickLinks, setHoveredQuickLinks] = React.useState(false);
  const [expandedItems, setExpandedItems] = React.useState<Record<string, boolean>>({});
  const navCooldownRef = React.useRef(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  const activeHorizontalIndex = useSelector(
    (state: RootState) => state.stepper.activeHorizontalIndex
  );

  const loanCategory = useSelector(
    (state: RootState) => state.auth?.loanCategory
  );

  const handleClearData = () => {
    dispatch(clearAllApplicantLoanDetails());
    dispatch(setAadhaarSeedingStatus(null));
    //dispatch(setIsEligible(false));
    dispatch(setDisableAddMoreCoApplicant(true));
    dispatch(clearSavedBasicCoApplicants());
  };

  /** Handle sidebar hover — blocked briefly after a navigation click */
  const handleMouseEnterSidebar = () => {
    if (!navCooldownRef.current) setSidebarOpen(true);
  };

  const handleMouseLeaveSidebar = () => {
    setSidebarOpen(false);
  };

  /** Handle quick links hover */
  const handleQuickLinksEnter = () => {
    setHoveredQuickLinks(true);
  };

  const handleQuickLinksLeave = () => {
    setHoveredQuickLinks(false);
  };
  /** Check if nav item is active */
  const isActive = (path: string) =>
    location.pathname === path ||
    (path !== "/" && location.pathname.startsWith(path));

  /** Handle navigation with loan validation */
  const navigateToDashboard = () => {
    // Coming to "Loans" from another page opens the loan dashboard on its first
    // tab, not the last (persisted) tab such as Payment. Clicking it while
    // already on the loan dashboard keeps the current tab.
    if (location.pathname !== "/dashboard") {
      dispatch(setSelectedIndex(0));
      dispatch(clearPartPaymentType());
      dispatch(cleardisbursementButtonSlice());
      localStorage.removeItem("termLoanRequestId");
    }

    const hasLoan =
      loanCategory?.Flexi?.length > 0 || loanCategory?.TermLoan?.length > 0;

    if (hasLoan) navigate("/dashboard");
    else {
      navigate("/dashboard");
    }
  };

  /** Centralized navigation logic */
  const processNavigation = (path: string) => {
    dispatch(setHorizontalSteps([]));
    dispatch(setActiveHorizontalIndex(0));
    dispatch(setIsBypassLogin(false));

    if (path === "/dashboard") navigateToDashboard();
    else navigate(path,{ replace: true });
  };

  const toggleExpanded = (id: string) => {
    setExpandedItems((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /** Click handler */
  const handleNavigate = (path: string) => {
    setSidebarOpen(false);
    navCooldownRef.current = true;
    setTimeout(() => { navCooldownRef.current = false; }, 700);
    handleClearData();
    const isFormScreen =
      activeHorizontalIndex <= 1 &&
      location.pathname === "/application/apply-now";

    if (isFormScreen) {
      setPendingPath(path);
      setConfirmModalOpen(true);
      return;
    }

    processNavigation(path);
  };

  return (
    <>
      <Box
        sx={{
          width: 80,
          bgcolor: "background.paper",
          py: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          borderRight: "1px solid #e0e0e0",
          position: "fixed",
          height: "100vh",
          top: 0,
          left: 0,
          zIndex: 1200,
        }}
        onMouseEnter={handleMouseEnterSidebar}
      >
        {/* Logo */}
        <IconButton sx={{ mb: 1 }} onClick={() => handleNavigate("/dashboard")}>
          <img
            src={Smalllogo}
            alt="Company Logo"
            title="Home"
            style={{ width: 32, height: 32 }}
          />
        </IconButton>

        {/* Navigation Items */}
        <List sx={{ width: "100%", mb: 4 }}>
          {navItems.map((item) => {
            const hasChildren = item.children && item.children.length > 0;
            const active =
              isActive(item.path) ||
              (hasChildren && item.children!.some((c) => isActive(c.path)));
            const iconColor = active ? "#fff" : "#D0D0D0";

            return (
              <ListItem key={item.id} disablePadding sx={{ mb: 1 }}>
                <Tooltip title={item.label} placement="right">
                  <ListItemButton
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      minHeight: 60,
                      borderRadius: 2,
                      mx: 1,
                      bgcolor: active ? "#00B3B5 !important" : "transparent",
                      color: active ? "#fff" : "text.secondary",
                      "&:hover": {
                        bgcolor: active ? "#00B3B5" : "rgba(0, 179, 181, 0.04)",
                        color: active ? "#fff" : "#00B3B5",
                      },
                    }}
                    selected={active}
                    onClick={() => handleNavigate(item.path)}
                  >
                    <ListItemIcon sx={{ minWidth: 0, color: "inherit" }}>
                      {React.isValidElement(item.icon)
                        ? React.cloneElement(item.icon, {
                            color: undefined,
                            style: { color: iconColor },
                          } as any)
                        : item.icon}
                    </ListItemIcon>
                  </ListItemButton>
                </Tooltip>
              </ListItem>
            );
          })}
        </List>

        <Box sx={{ flexGrow: 1 }} />

        {/* Help Button */}
        <Tooltip title="Help" placement="right">
          <IconButton sx={{ mb: 1 }}>
            <img src={HelpIcon} alt="Quick Links" style={{ width: 22 }} />
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded Sidebar Drawer */}
      <Drawer
        anchor="left"
        open={sidebarOpen}
        variant="persistent"
        hideBackdrop={true}
        ModalProps={{
          keepMounted: true,
          disableEnforceFocus: true,
          disableAutoFocus: true,
        }}
        PaperProps={{
          onMouseEnter: handleMouseEnterSidebar,
          onMouseLeave: handleMouseLeaveSidebar,
          sx: {
            width: 250,
            marginLeft: 0,
            height: "100vh",
            bgcolor: "background.paper",
            borderRight: "1px solid #e0e0e0",
            boxShadow: "2px 0 8px rgba(0,0,0,0.15)",
          },
        }}
      >
        <Box
          sx={{
            py: 2,
            px: 1,
            display: "flex",
            flexDirection: "column",
            height: "100%",
          }}
        >
          {/* Logo Section */}
          <Box
            sx={{
              display: "flex",
              justifyContent: 'center',
              alignItems: "center",
              mb: 3,
              px: 2,
            }}
          >
            <img
              src={CompanyLogo}
              alt="Company Logo"
              style={{
                height: 40,
                objectFit: "contain",
              }}
            />
            {/* <img
              src={Smalllogo}
              alt="Company Logo"
              style={{ width: 32, height: 32 }}
            /> */}
          </Box>

          {/* Navigation Items */}
          <List sx={{ flexGrow: 1 }}>
            {navItems.map((item) => {
              const hasChildren = item.children && item.children.length > 0;
              const isOpen = expandedItems[item.id] ?? false;
              const active = isActive(item.path) ||
                (hasChildren && item.children!.some((c) => isActive(c.path)));

              return (
                <React.Fragment key={item.id}>
                  <ListItem disablePadding sx={{ mb: 0.5 }}>
                    <ListItemButton
                      sx={{
                        borderRadius: 2,
                        mx: 1,
                        bgcolor: active ? "#00B3B5 !important" : "transparent",
                        color: active ? "#fff" : "text.primary",
                        "&:hover": {
                          bgcolor: active ? "#00B3B5" : "rgba(0, 179, 181, 0.08)",
                          color: active ? "#fff" : "#00B3B5",
                        },
                        py: 1.5,
                      }}
                      selected={active}
                      onClick={() =>
                        hasChildren ? toggleExpanded(item.id) : handleNavigate(item.path)
                      }
                    >
                      <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={item.label}
                        sx={{
                          "& .MuiListItemText-primary": {
                            fontSize: "0.95rem",
                            fontWeight: active ? 600 : 500,
                          },
                        }}
                      />
                      {hasChildren && (isOpen ? <ExpandLess /> : <ExpandMore />)}
                    </ListItemButton>
                  </ListItem>

                  {hasChildren && (
                    <Collapse in={isOpen} unmountOnExit>
                      <List disablePadding>
                        {item.children!.map((child) => {
                          const childActive = isActive(child.path);
                          return (
                            <ListItem key={child.id} disablePadding>
                              <ListItemButton
                                sx={{
                                  borderRadius: 2,
                                  mx: 1,
                                  pl: 7,
                                  py: 1,
                                  bgcolor: childActive ? "#00B3B5 !important" : "transparent",
                                  color: childActive ? "#fff" : "text.secondary",
                                  "&:hover": {
                                    bgcolor: "rgba(0, 179, 181, 0.08)",
                                    color: "#00B3B5",
                                  },
                                }}
                                selected={childActive}
                                onClick={() => handleNavigate(child.path)}
                              >
                                <ListItemText
                                  primary={child.label}
                                  sx={{
                                    "& .MuiListItemText-primary": {
                                      fontSize: "0.875rem",
                                      fontWeight: childActive ? 600 : 400,
                                    },
                                  }}
                                />
                              </ListItemButton>
                            </ListItem>
                          );
                        })}
                      </List>
                    </Collapse>
                  )}
                </React.Fragment>
              );
            })}
          </List>

          {/* Quick Links Section */}
          <Box sx={{ px: 1, pt: 2, borderTop: "1px solid #e0e0e0" }}>
            <ListItemButton
              id="quickLinks-anchor"
              sx={{
                borderRadius: 2,
                py: 1.5,
                "&:hover": {
                  bgcolor: "rgba(0, 179, 181, 0.08)",
                  color: "#00B3B5",
                },
              }}
              onClick={handleQuickLinksEnter}
              //onMouseLeave={handleQuickLinksLeave}
            >
              <ListItemIcon sx={{ color: "inherit", minWidth: 40 }}>
                <img src={HelpIcon} alt="Quick Links" style={{ width: 22 }} />
              </ListItemIcon>
              <ListItemText
                primary="QuickLinks"
                sx={{
                  "& .MuiListItemText-primary": {
                    fontSize: "0.95rem",
                    fontWeight: 500,
                  },
                }}
              />
            </ListItemButton>

            {/* Quick Links Menu */}
            {hoveredQuickLinks && ROUTES?.QuickLinks?.QuickLinks && (
              <Menu
                anchorEl={document.getElementById("quickLinks-anchor")}
                open={hoveredQuickLinks}
                onClose={() => setHoveredQuickLinks(false)}
                MenuListProps={{
                  onMouseLeave: () => setHoveredQuickLinks(false),
                  onMouseEnter: () => setHoveredQuickLinks(true),
                }}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
                sx={{
                  "& .MuiPaper-root": {
                    mt: 0.5,
                    minWidth: 200,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                    border: "1px solid #e0e0e0",
                    borderRadius: 2,
                  },
                }}
              >
                {ROUTES.QuickLinks?.QuickLinks?.map(
                  (quickLink: any, index: number) => (
                    <MenuItem
                      key={index}
                      onClick={() => {
                        setSidebarOpen(false);
                        setHoveredQuickLinks(false);
                        navCooldownRef.current = true;
                        setTimeout(() => { navCooldownRef.current = false; }, 700);
                        if (quickLink.isInternal) navigate(quickLink.path);
                        else window.open(quickLink.path, "_blank");
                      }}
                      sx={{
                        fontSize: "0.9rem",
                        py: 1.5,
                        px: 2,
                        minHeight: 40,
                        "&:hover": {
                          bgcolor: "rgba(0, 179, 181, 0.08)",
                          color: "#00B3B5",
                        },
                        "&:not(:last-child)": {
                          borderBottom: "1px solid #f0f0f0",
                        },
                      }}
                    >
                      <span style={{ color: "inherit" }}>
                        {quickLink.label}
                      </span>
                    </MenuItem>
                  )
                )}
              </Menu>
            )}
          </Box>
        </Box>
      </Drawer>

      {/* Confirmation Modal */}
       {confirmModalOpen && (
        <ModalPopup
          isOpen={confirmModalOpen}
          onClose={() => setConfirmModalOpen(false)}
          title="Confirm Navigation"
        >
          <Box sx={{ p: 0 }}>
            <Typography>Are you sure about your action?</Typography>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: 3,
                gap: 2,
              }}
            >
              <Button
                onClick={() => setConfirmModalOpen(false)}
                color="primary"
                size="small"
                variant="outlined"
              >
                No
              </Button>
              <PrimaryButton
                onClick={() => {
                  setConfirmModalOpen(false);
                  processNavigation(pendingPath);
                }}
                variant="contained"
              >
                Yes
              </PrimaryButton>
            </Box>
          </Box>
        </ModalPopup>
      )}
    </>
  );
};

export default Sidebar;

     
